import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { unregisterFcmServiceWorker } from "../AuthProvider";
import { SESSION_EXPIRED_MESSAGE } from "../constants/auth-constant";
import { appPaths } from "../constants/paths";
import { queryClient } from "../queries/query-client";
import { useLogOut } from "../queries/use-Logout";
import { beginLogout, getRefreshToken } from "../utils/auth-storage";
import { runLogoutTasks } from "../utils/logout-tasks";
import { clearSession } from "../utils/session-teardown";
import { useSnackbar } from "./useSnackbar";

/**
 * @description single log out path for the app — tears the session down in an
 * order that keeps the transition to the login page smooth:
 *
 * 1. flag the logout so a 401 from an in-flight request cannot trigger the
 *    interceptor's session-expired redirect,
 * 2. stop React Query (cancel in-flight, drop the cache) so no component
 *    refetches with a token that is about to disappear,
 * 3. fire the logout API while the tokens still exist, but do not wait for it —
 *    the user should not sit on the app watching a dead session,
 * 4. clear storage and the session stores, then navigate to login with
 *    `replace` so back does not re-enter the app.
 *
 * The server-side work (logout API, FCM unregister) finishes in the background
 * after the navigation; failures there are logged, never surfaced, and never
 * reload the page.
 *
 * @returns logOut fnc
 *
 * @author [Rajkumar Rathod]
 */
export const useAuth = () => {
  const navigate = useNavigate();
  const { mutateAsync: LogOutReq } = useLogOut();
  const showSnackbar = useSnackbar();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const logout = useCallback(
    async (isUserIdleForLongTime?: boolean) => {
      const refreshToken = getRefreshToken();

      beginLogout();
      setIsLoggingOut(true);

      try {
        // Let pages persist unsaved work (e.g. an open encounter note) while the
        // tokens are still present, before anything below tears the session down.
        await runLogoutTasks({ isIdle: Boolean(isUserIdleForLongTime) });

        await queryClient.cancelQueries();

        /**
         * Started before the tokens are cleared so the request still carries a
         * valid Authorization header, and deliberately not awaited so the
         * navigation below is immediate.
         */
        const serverLogout = refreshToken
          ? LogOutReq({
              refreshToken,
              expireSession: isUserIdleForLongTime,
            }).catch((error) => {
              console.error(
                "Logout API failed, but the session is already cleared:",
                error
              );
            })
          : Promise.resolve();

        clearSession();
        navigate(appPaths.login, { replace: true });

        if (isUserIdleForLongTime)
          showSnackbar({ type: "error", message: SESSION_EXPIRED_MESSAGE });

        void Promise.allSettled([serverLogout, unregisterFcmServiceWorker()]);
      } finally {
        setIsLoggingOut(false);
      }
    },
    [LogOutReq, navigate, showSnackbar]
  );

  return { logout, isLoggingOut };
};
